import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { Habit, Member, RedemptionItem, RedemptionRecord } from '../types';
import { HealthProfile, HealthRecord } from '../healthTypes';

interface HabitSyncDB extends DBSchema {
    habits: {
        key: string;
        value: Habit;
    };
    members: {
        key: string;
        value: Member;
    };
    redemption_items: {
        key: string;
        value: RedemptionItem;
    };
    redemption_records: {
        key: string;
        value: RedemptionRecord;
    };
    health_profiles: {
        key: string;
        value: HealthProfile;
    };
    health_records: {
        key: string;
        value: HealthRecord;
    };
}

const DB_NAME = 'habitsync_db';
const DB_VERSION = 3; // Incremented for health data

let dbPromise: Promise<IDBPDatabase<HabitSyncDB>> | null = null;

export const getDB = () => {
    if (!dbPromise) {
        dbPromise = openDB<HabitSyncDB>(DB_NAME, DB_VERSION, {
            upgrade(db, oldVersion, newVersion, transaction) {
                if (!db.objectStoreNames.contains('habits')) {
                    db.createObjectStore('habits', { keyPath: 'id' });
                }
                if (!db.objectStoreNames.contains('members')) {
                    db.createObjectStore('members', { keyPath: 'id' });
                }
                if (!db.objectStoreNames.contains('redemption_items')) {
                    db.createObjectStore('redemption_items', { keyPath: 'id' });
                }
                if (!db.objectStoreNames.contains('redemption_records')) {
                    db.createObjectStore('redemption_records', { keyPath: 'id' });
                }
                if (!db.objectStoreNames.contains('health_profiles')) {
                    db.createObjectStore('health_profiles', { keyPath: 'id' });
                }
                if (!db.objectStoreNames.contains('health_records')) {
                    db.createObjectStore('health_records', { keyPath: 'id' });
                }
            },
        });
    }
    return dbPromise;
};

export const getAllHabitsFromDB = async (): Promise<Habit[]> => {
    const db = await getDB();
    return db.getAll('habits');
};

export const saveHabitsToDB = async (habits: Habit[]) => {
    const db = await getDB();
    const tx = db.transaction('habits', 'readwrite');
    const store = tx.objectStore('habits');

    // 类似于 localStorage SET操作，先清空再写入，确保删除的数据也被移除
    await store.clear();
    // 批量写入
    for (const habit of habits) {
        await store.put(habit);
    }
    await tx.done;
};

export const getAllMembersFromDB = async (): Promise<Member[]> => {
    const db = await getDB();
    return db.getAll('members');
};

export const saveMembersToDB = async (members: Member[]) => {
    const db = await getDB();
    const tx = db.transaction('members', 'readwrite');
    const store = tx.objectStore('members');
    await store.clear();
    for (const member of members) {
        await store.put(member);
    }
    await tx.done;
};

export const getAllRedemptionItemsFromDB = async (): Promise<RedemptionItem[]> => {
    const db = await getDB();
    return db.getAll('redemption_items');
};

export const saveRedemptionItemsToDB = async (items: RedemptionItem[]) => {
    const db = await getDB();
    const tx = db.transaction('redemption_items', 'readwrite');
    const store = tx.objectStore('redemption_items');
    await store.clear();
    for (const item of items) {
        await store.put(item);
    }
    await tx.done;
};

export const getAllRedemptionRecordsFromDB = async (): Promise<RedemptionRecord[]> => {
    const db = await getDB();
    return db.getAll('redemption_records');
};

export const saveRedemptionRecordsToDB = async (records: RedemptionRecord[]) => {
    const db = await getDB();
    const tx = db.transaction('redemption_records', 'readwrite');
    const store = tx.objectStore('redemption_records');
    await store.clear();
    for (const record of records) {
        await store.put(record);
    }
    await tx.done;
};

// Health Profiles
export const getAllHealthProfilesFromDB = async (): Promise<HealthProfile[]> => {
    const db = await getDB();
    return db.getAll('health_profiles');
};

export const saveHealthProfilesToDB = async (profiles: HealthProfile[]) => {
    const db = await getDB();
    const tx = db.transaction('health_profiles', 'readwrite');
    const store = tx.objectStore('health_profiles');
    await store.clear();
    for (const profile of profiles) {
        await store.put(profile);
    }
    await tx.done;
};

// Health Records
export const getAllHealthRecordsFromDB = async (): Promise<HealthRecord[]> => {
    const db = await getDB();
    return db.getAll('health_records');
};

export const saveHealthRecordsToDB = async (records: HealthRecord[]) => {
    const db = await getDB();
    const tx = db.transaction('health_records', 'readwrite');
    const store = tx.objectStore('health_records');
    await store.clear();
    for (const record of records) {
        await store.put(record);
    }
    await tx.done;
};
